import { useCallback, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
} from '@react-google-maps/api';

import { envVariables } from '@/constants/index';

import styles from '../GoogleMapsClusterer/google-maps.module.scss';

const defaultOptions = {
  panControl: true,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  clickableIcons: false,
  keyboardShortcuts: false,
  scrollWeel: false,
  disableDoubleClickZoom: false,
  fullscreenControl: true,
};

const defaultLocation = {
  lat: 40.3763,
  lng: 49.81567,
};

const GoogleMaps = ({
  className,
  markers = [],
  defaultCoords = '',
  disableMarkerClick = false,
  customerLocation,
  customOptions,
  customPin,
  withoutInfoWindow,
  customCenter,
}) => {
  const { t } = useTranslation();
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(customCenter ?? defaultLocation);
  const [activeMarker, setActiveMarker] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    region: 'AZ',
  });
  useEffect(() => {
    document.body.classList.add('section-small');

    return () => {
      document.body.classList.remove('section-small');
    };
  }, [center]);

  const onLoad = useCallback(
    (map) => {
      const bounds = new window.google.maps.LatLngBounds(center);
      map.fitBounds(bounds);
      setMap(map);
    },
    [center],
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleActiveMarker = useCallback(
    (marker, coords, bounds = map.getBounds()) => {
      setActiveMarker(null);
      setTimeout(() => {
        setActiveMarker(marker);

        if (coords) {
          const span = bounds.toSpan();

          map.panTo({
            ...coords,
            lat: coords.lat + span?.lat() * 0.28,
            lng: coords.lng + span?.lng() * 0.2,
          });
        }
      }, 50);
    },
    [map],
  );
  useEffect(() => {
    if (map && markers.length) {
      setCenter(
        defaultCoords || {
          lat: Number(markers[0]?.lat),
          lng: Number(markers[0]?.lng),
        },
      );
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((item) => {
        bounds.extend({
          lat: Number(item?.lat),
          lng: Number(item?.lng),
        });
      });

      map.fitBounds(bounds);

      const span = bounds.toSpan();

      map.panTo({
        ...center,
        lat: center.lat + span?.lat() * 0.4,
      });

      let ind = 0;
      if (defaultCoords) {
        ind = markers.findIndex(
          (marker) =>
            Number(marker.lat) === defaultCoords.lat &&
            Number(marker.lng) === defaultCoords.lng,
        );
      }
      handleActiveMarker(ind, defaultCoords, bounds);
    }
  }, [map, defaultCoords, markers, handleActiveMarker]);
  const renderMarkers = (item, index) => {
    const icon = {
      url: customPin || '/assets/mapMarker.png',
      scaledSize: new window.google.maps.Size(50, 50),
    };
    return (
      <MarkerF
        onUnmount={() => setActiveMarker(null)}
        key={index}
        position={{
          lat: Number(item?.lat),
          lng: Number(item?.lng),
        }}
        icon={icon}
        onClick={() => {
          setActiveMarker(null);
          if (!disableMarkerClick && item?.lat && item?.lng) {
            handleActiveMarker(index, {
              lat: Number(item?.lat),
              lng: Number(item?.lng),
            });
          }
        }}
      >
        {activeMarker === index && !withoutInfoWindow && (
          <InfoWindowF
            options={{
              disableAutoPan: true,
            }}
            // onUnmount={() => setActiveMarker(null)}
            itemScope
            itemType='https://schema.org/Property'
          >
            <div className={styles.infoWindow}>
              {!!item?.name && (
                <div className={styles.infoWindowTitle}>{item?.name}</div>
              )}
              {!!item?.description && (
                <div className={styles.infoWindowAddress}>
                  {item?.description}
                </div>
              )}

              {/* {!!city && <div className={styles.infoWindowCity}>{city}</div>}
              {!!street && (
                <div className={styles.infoWindowStreet}>
                  {`${street} ${house ? `, ${house}` : ''}`}
                </div>
              )} */}
              {/* {!!coverage && (
                <div className={styles.infoWindowCoverage}>
                  {`Coverage: ${coverage}`}
                </div>
              )} */}

              {item?.working_hours?.length > 0 && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:workingHours')}</div>
                  <div className={styles.value}>
                    {item?.working_hours?.map((item) => (
                      <div
                        className={`${styles.hours} grid-extend`}
                        key={item?.id}
                      >
                        <span>{item?.days}:</span>
                        <span>{item?.working_hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {item?.email && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:email')}</div>
                  <div className={`${styles.value} grid-extend`}>
                    <div>{item?.email}</div>
                  </div>
                </div>
              )}

              {item?.phone_number && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:phone')}</div>
                  <div className={`${styles.value} grid-extend`}>
                    <div>{item?.phone_number}</div>
                  </div>
                </div>
              )}
            </div>
          </InfoWindowF>
        )}
      </MarkerF>
    );
  };

  return (
    <div
      className={`${className} ${styles.map}`}
      itemScope
      itemType='https://schema.org/Place'
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: '100%',
          }}
          center={
            Object.keys(customerLocation).length !== 0
              ? customerLocation
              : center
          }
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={customOptions ?? defaultOptions}
          onClick={() => setActiveMarker(null)}
        >
          {!!markers.length && markers.map(renderMarkers)}
        </GoogleMap>
      )}
    </div>
  );
};

export default GoogleMaps;
